/**
 * 人員列表增強版組件 | Enhanced Person List Component
 * 支援編輯、刪除、新增、歷史記錄查看 | Supports edit, delete, create, history viewing
 */

import { useState, useCallback } from 'react';
import { Person } from '@/types/person';
import { useResourceStore } from '@/store/resourceStore';
import { PersonForm } from './PersonForm';
import { clearAllStorage, savePersonsToStorage } from '@/utils/localStorage';
import { toast } from '@/components/UI/Toast';
import { ConfirmDialog } from '@/components/UI/Modal';

interface PersonListEnhancedProps {
  onViewHistory: () => void;  // 切換到歷史視圖 | Switch to history view
  onViewShift?: () => void;    // 切換到班表視圖 | Switch to shift view
}

export function PersonListEnhanced({ onViewHistory, onViewShift }: PersonListEnhancedProps) {
  const { persons, deletePerson } = useResourceStore();

  // UI 狀態 | UI state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Person | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | '啟用' | '停用'>('all');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');

  // 刪除確認對話框狀態 | Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ person: Person; reason: string } | null>(null);

  // 清除快取確認對話框狀態 | Clear storage confirmation dialog state
  const [clearStorageConfirm, setClearStorageConfirm] = useState(false);

  // 取得所有唯一部門 | Get all unique departments
  const departments = Array.from(new Set(persons.map((p) => p.department))).sort();

  // 過濾後的人員列表 | Filtered person list
  const filteredPersons = persons.filter((person) => {
    // 搜尋過濾 | Search filter
    const matchesSearch =
      searchTerm === '' ||
      person.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.email.toLowerCase().includes(searchTerm.toLowerCase());

    // 狀態過濾 | Status filter
    const matchesStatus = filterStatus === 'all' || person.status === filterStatus;

    // 部門過濾 | Department filter
    const matchesDepartment =
      filterDepartment === 'all' || person.department === filterDepartment;

    return matchesSearch && matchesStatus && matchesDepartment;
  });

  // 開啟編輯表單 | Open edit form
  const handleEdit = useCallback((person: Person) => {
    setEditingPerson(person);
    setIsFormOpen(true);
  }, []);

  // 開啟新增表單 | Open create form
  const handleCreate = useCallback(() => {
    setEditingPerson(undefined);
    setIsFormOpen(true);
  }, []);

  // 刪除人員 | Delete person
  const handleDelete = useCallback(
    (person: Person) => {
      // 開啟確認對話框，原因設為空字串讓使用者可選填
      setDeleteConfirm({ person, reason: '' });
    },
    []
  );

  // 確認刪除 | Confirm delete
  const confirmDelete = useCallback(() => {
    if (!deleteConfirm) return;

    deletePerson(deleteConfirm.person.id, deleteConfirm.reason || undefined);
    // 保存到 localStorage
    savePersonsToStorage(persons.filter((p) => p.id !== deleteConfirm.person.id));

    setDeleteConfirm(null);
    toast.success(
      '已刪除人員',
      `人員「${deleteConfirm.person.name}」已被刪除`,
      3000
    );
  }, [deleteConfirm, deletePerson, persons]);

  // 關閉表單 | Close form
  const handleCloseForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingPerson(undefined);
  }, []);

  // 保存後回調 | After save callback
  const handleSave = useCallback((person: Person) => {
    // 保存到 localStorage
    savePersonsToStorage(
      editingPerson
        ? persons.map((p) => (p.id === editingPerson.id ? person : p))
        : [...persons, person]
    );
    setIsFormOpen(false);
    setEditingPerson(undefined);
  }, [editingPerson, persons]);

  // 清除 localStorage | Clear localStorage
  const handleClearStorage = useCallback(() => {
    setClearStorageConfirm(true);
  }, []);

  // 確認清除 | Confirm clear
  const confirmClearStorage = useCallback(() => {
    clearAllStorage();
    toast.success(
      '已清除資料',
      '所有本地儲存資料已清除',
      3000
    );
    setClearStorageConfirm(false);
  }, []);

  return (
    <div className="space-y-4">
      {/* 標題與操作列 | Header and action row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-bold text-[#0f172a]">👥 人員管理</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleCreate}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-sm font-semibold shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 active:scale-95"
          >
            ➕ 新增人員
          </button>
          {onViewShift && (
            <button
              onClick={onViewShift}
              className="px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-500 hover:from-indigo-700 hover:to-purple-600 text-white rounded-lg transition-all text-sm font-semibold shadow-md shadow-indigo-500/20 hover:shadow-lg hover:shadow-indigo-500/30 active:scale-95"
            >
              📅 班表管理
            </button>
          )}
          <button
            onClick={onViewHistory}
            className="px-5 py-2.5 bg-white hover:bg-slate-50 text-slate-700 border-2 border-slate-200 hover:border-slate-300 rounded-lg transition-all text-sm font-semibold shadow-sm hover:shadow active:scale-95"
          >
            📜 查看歷史
          </button>
          <button
            onClick={handleClearStorage}
            className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg transition-all text-sm font-semibold shadow-md shadow-red-500/20 hover:shadow-lg hover:shadow-red-500/30 active:scale-95"
          >
            🗑️ 清除快取
          </button>
        </div>
      </div>

      {/* 搜尋與過濾 | Search and filter */}
      <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 搜尋框 | Search */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">
              🔍 搜尋
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜尋工號、姓名、郵件..."
              className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-lg text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all"
            />
          </div>

          {/* 狀態過濾 | Status filter */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">
              狀態
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all cursor-pointer"
            >
              <option value="all">全部</option>
              <option value="啟用">啟用</option>
              <option value="停用">停用</option>
            </select>
          </div>

          {/* 部門過濾 | Department filter */}
          <div>
            <label className="block text-sm font-semibold text-slate-600 mb-2">
              部門
            </label>
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all cursor-pointer"
            >
              <option value="all">全部</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 統計資訊 | Statistics */}
        <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap gap-6 text-sm">
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
            總人數：<strong className="text-slate-800 font-bold">{persons.length}</strong>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            啟用：<strong className="text-green-600 font-bold">{persons.filter((p) => p.status === '啟用').length}</strong>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            停用：<strong className="text-red-600 font-bold">{persons.filter((p) => p.status === '停用').length}</strong>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            顯示：<strong className="text-blue-600 font-bold">{filteredPersons.length}</strong>
          </span>
        </div>
      </div>

      {/* 人員列表表格 | Person list table */}
      {filteredPersons.length === 0 ? (
        <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <div className="text-5xl mb-4">📭</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">沒有符合條件的人員</h3>
          <p className="text-slate-600 mb-6 max-w-md mx-auto">
            請嘗試調整搜尋關鍵字或過濾器條件，或新增人員資料
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterStatus('all');
                setFilterDepartment('all');
              }}
              className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
            >
              清除過濾
            </button>
            <button
              onClick={handleCreate}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl active:scale-95"
            >
              ➕ 新增人員
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    工號
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    姓名
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    部門
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    廠別
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    角色
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    班別
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">
                    狀態
                  </th>
                  <th className="px-5 py-4 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPersons.map((person, index) => (
                  <tr key={person.id} className={`hover:bg-blue-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                    <td className="px-5 py-4 whitespace-nowrap text-sm font-bold text-slate-800">
                      {person.id}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-slate-700">
                      {person.name}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-slate-600">
                      {person.department}
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <div className="flex flex-wrap gap-1.5">
                        {person.plants.map((plant) => (
                          <span
                            key={plant}
                            className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium border border-blue-200"
                          >
                            {plant}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm">
                      <div className="flex flex-wrap gap-1.5">
                        {person.roles.map((role) => (
                          <span
                            key={role}
                            className="px-2.5 py-1 bg-violet-50 text-violet-700 rounded-md text-xs font-medium border border-violet-200"
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-slate-600">
                      {person.shiftType}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-3.5 py-1.5 rounded-full text-xs font-bold shadow-sm ${
                          person.status === '啟用'
                            ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-300'
                            : 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border border-red-300'
                        }`}
                      >
                        {person.status}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(person)}
                          className="px-3.5 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-xs font-semibold shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-95"
                          title="編輯"
                        >
                          編輯
                        </button>
                        <button
                          onClick={() => handleDelete(person)}
                          className="px-3.5 py-2 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg transition-all text-xs font-semibold shadow-md shadow-red-500/20 hover:shadow-lg active:scale-95"
                          title="刪除"
                        >
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 編輯/新增表單 Modal | Edit/Create modal */}
      {isFormOpen && (
        <PersonForm
          person={editingPerson}
          onClose={handleCloseForm}
          onSave={handleSave}
        />
      )}

      {/* 刪除確認對話框 | Delete confirmation dialog */}
      {deleteConfirm && (
        <ConfirmDialog
          isOpen={true}
          title="確認刪除人員"
          message={`確定要刪除人員「${deleteConfirm.person.name}」嗎？此操作無法復原。`}
          confirmLabel="確認刪除"
          cancelLabel="取消"
          type="danger"
          onClose={() => setDeleteConfirm(null)}
          onConfirm={confirmDelete}
        />
      )}

      {/* 清除快取確認對話框 | Clear storage confirmation dialog */}
      {clearStorageConfirm && (
        <ConfirmDialog
          isOpen={true}
          title="確認清除資料"
          message="確定要清除所有 localStorage 資料嗎？這將清除本網站的所有本地儲存資料。"
          confirmLabel="確認清除"
          cancelLabel="取消"
          type="warning"
          onClose={() => setClearStorageConfirm(false)}
          onConfirm={confirmClearStorage}
        />
      )}
    </div>
  );
}
